import React, { useEffect, useMemo, useRef, useState } from "react";
import CustomTable from "../CustomTable";
import CustomButton from "../CustomButton";
import moment from "moment";
import DatePicker from "../FormFields/DatePicker";
import useApi from "../../hooks/useApi";
import { apiUrls } from "../../utils/api/apiUrls";
import { storeColumns } from "../Columns/Vouchers/AllVouchers";
import { l1Columns } from "../Columns/Vouchers/PendingVouchers";
import { useAuth } from "../../contexts/AuthContext";
import { downloadReports, downloadReportsFun, getArrayOfSelectedStoresCode } from "../../utils/helpers";
import {
  useNavigate,
  createSearchParams,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import { toast } from "react-toastify";
import {
  normalizeVoucherParentRowForStoreColumns,
  resolveVoucherListRowId,
  storeVoucherParentsFromGetRecordsResponse,
} from "../../utils/dmsStandaloneListUtils";

/** Parse ?startDate / ?endDate without moment string heuristics (avoids deprecation warnings). */
function dateFromSearchParam(param, fallback) {
  if (param == null || String(param).trim() === "") {
    return fallback;
  }
  const d = new Date(param);
  return Number.isNaN(d.getTime()) ? fallback : d;
}

const ReworkHistory = () => {
  // Do not default `data` to [] — it breaks `storeReworkListSource` vs `nonProcessedData` and makes
  // `tableData === []` truthy so the table never uses computed rows (V.ID looks dead / empty).
  const { data, nonProcessedData, apiCall } = useApi();
  const {data: fileData,  apiCall: fileDownload } = useApi();
  const {data: fileStoreData,  apiCall: fileDownloadAtStore } = useApi();

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const loc = useLocation()
  const [startDate, setStartDate] = useState(() =>
    dateFromSearchParam(
      searchParams.get("startDate"),
      moment().subtract(7, "days").toDate()
    )
  );
  const [endDate, setEndDate] = useState(() =>
    dateFromSearchParam(searchParams.get("endDate"), new Date())
  );
  const {
    user: { userLabel = "", access_token: accessToken } = {},
    selectedStores,
    storeDropdownState,
    currentStoreSelect, 
    setCurrentStoreSelect
  } = useAuth();

  const { name: storeName } =
    selectedStores?.storeDetails || selectedStores?.[0] || {name:""};
  const selectedStoreCode =
    selectedStores?.storeDetails?.storeCode ||
    selectedStores?.storeCode ||
    selectedStores?.[0]?.storeCode ||
    "";

  const [selectedCheckBoxes, setSelectedCheckBoxes] = useState([]);
  const [tableData, setTableData] = useState();
  const inFlightRequestKeyRef = useRef(null);
  const lastCompletedRequestKeyRef = useRef(null);

  let pathVar = loc?.pathname?.split("/") || ""
  let subVoucherPath = pathVar[pathVar.length - 1]

  const storeListRowsFromApi = (payload) => {
    if (!payload) return [];
    if (Array.isArray(payload)) {
      return payload.map(normalizeVoucherParentRowForStoreColumns);
    }
    return storeVoucherParentsFromGetRecordsResponse(payload);
  };

  /** Store REWORK payload is storeWiseRecordsResponses, not storeGetResponse — prefer full API object. */
  const storeReworkListSource = useMemo(() => {
    if (!userLabel || userLabel !== "Store") return null;
    const isPlainObject =
      data && typeof data === "object" && !Array.isArray(data);
    return isPlainObject ? data : nonProcessedData;
  }, [userLabel, data, nonProcessedData]);

  /** Rows shown in the table: non-empty `tableData`, else computed from API (Store must not use `[]` as truthy guard). */
  const reworkTableRows = useMemo(() => {
    if (subVoucherPath === "sub-voucher") {
      return Array.isArray(tableData) ? tableData : [];
    }
    if (Array.isArray(tableData) && tableData.length > 0) {
      return tableData;
    }
    if (userLabel === "Store") {
      return storeListRowsFromApi(storeReworkListSource || {});
    }
    return data?.storeGetResponse || data?.storeWiseRecordsResponses || [];
  }, [
    subVoucherPath,
    tableData,
    userLabel,
    storeReworkListSource,
    data?.storeGetResponse,
    data?.storeWiseRecordsResponses,
  ]);

  const mapSubVoucherData = () =>{
    let currentSubVoucherData = [];
    const storeData = Array.isArray(data)
      ? nonProcessedData?.storeGetResponse ||
        nonProcessedData?.storeWiseRecordsResponses ||
        []
      : data?.storeGetResponse || data?.storeWiseRecordsResponses || [];
    const getStoreCode = (row) =>
      row?.storeCode || row?.store?.storeCode || row?.store?.code || "";
    const getStoreName = (row) =>
      row?.storeName || row?.store?.name || row?.store?.storeName || "";
    const getStoreChildren = (row) => {
      const docs = Array.isArray(row?.documents) ? row.documents : [];
      if (docs.length > 0) return docs;
      return Array.isArray(row?.dataModalRecordsResponse)
        ? row.dataModalRecordsResponse
        : [];
    };
    const findStore = (codeHint, nameHint) => {
      const normalizedCode = String(codeHint || "").trim().toLowerCase();
      const normalizedName = String(nameHint || "").trim().toLowerCase();
      if (normalizedCode) {
        const byCode = storeData.find(
          (i) => getStoreCode(i)?.toLowerCase() === normalizedCode
        );
        if (byCode) return byCode;
      }
      if (normalizedName) {
        const byName = storeData.find(
          (i) => getStoreName(i)?.toLowerCase() === normalizedName
        );
        if (byName) return byName;
      }
      return storeData?.find((i) => getStoreChildren(i).length > 0) || null;
    };
      if(currentStoreSelect){
        const selectedStore = findStore("", currentStoreSelect);
        currentSubVoucherData = getStoreChildren(selectedStore);
      }else if(storeName && storeName !== 'All'){
        const selectedStore = findStore(selectedStoreCode, storeName);
        currentSubVoucherData = getStoreChildren(selectedStore);
      }else{
        currentSubVoucherData = storeData?.length > 0 
          ? getStoreChildren(storeData[0])
          : [];
      }
      return currentSubVoucherData.map(normalizeVoucherParentRowForStoreColumns);
  }

  useEffect(() => {
    if (subVoucherPath === "sub-voucher") {
      setTableData(mapSubVoucherData() || []);
    }
  }, [data, nonProcessedData, subVoucherPath, userLabel]);

  useEffect(() => {
    let nextData = [];
    if (subVoucherPath === "vouchers") {
      if (userLabel === "Store") {
        nextData = storeListRowsFromApi(storeReworkListSource || {});
      } else {
        nextData = data?.storeGetResponse || data?.storeWiseRecordsResponses || [];
      }
    } else {
      nextData = mapSubVoucherData();
    }
    setTableData(nextData);
  }, [subVoucherPath, data, nonProcessedData, userLabel, storeReworkListSource]);

  const voucherApiCall = async (start, end) => {
    const safeStart = start || new Date();
    const safeEnd = end || safeStart;
    const startTs = moment(safeStart).startOf("d").format("YYYY-MM-DD HH:mm:ss");
    const endTs = moment(safeEnd).endOf("d").format("YYYY-MM-DD HH:mm:ss");
    const storeCodes = getArrayOfSelectedStoresCode(selectedStores, storeDropdownState);
    const normalizedStoreCodes = Array.isArray(storeCodes) ? [...storeCodes].sort() : [];
    const requestKey = `${startTs}|${endTs}|${normalizedStoreCodes.join(",")}`;
    if (inFlightRequestKeyRef.current === requestKey || lastCompletedRequestKeyRef.current === requestKey) {
      return;
    }
    inFlightRequestKeyRef.current = requestKey;
    navigate(
      {
        pathname: window.location.pathname,
        search: createSearchParams({
          startDate: moment(safeStart).toISOString(),
          endDate: moment(safeEnd).toISOString(),
        }).toString(),
      },
      { replace: true }
    );
    try {
      await apiCall({
        url: apiUrls.dmsService.getRecords,
        dataToSend: {
          startDate: startTs,
          metadataSchemaId: 4,
          endDate: endTs,
          searchText: "",
          status: "REWORK",
          storeCodes: normalizedStoreCodes,
        },
        method: "post",
        // Store REWORK uses storeWiseRecordsResponses (child rows grouped by store), not storeGetResponse.
        // processData + storeGetResponse would set data to []/undefined and the table shows "No data".
        onSuccess: () => {
          // Only mark completed after a real 200 + useApi success. If apiCall bails early (no token),
          // `lastCompleted` must not be set or the same requestKey is blocked forever and steps 2–3 never happen.
          lastCompletedRequestKeyRef.current = requestKey;
        },
      });
    } finally {
      if (inFlightRequestKeyRef.current === requestKey) {
        inFlightRequestKeyRef.current = null;
      }
    }
  };

  const isStoreActive = userLabel === "Store";
  const storeFetchDependencyKey = useMemo(() => {
    const selectedCode =
      selectedStores?.storeDetails?.storeCode ||
      selectedStores?.storeCode ||
      selectedStores?.value ||
      "";
    const dropdownCount = Array.isArray(storeDropdownState)
      ? storeDropdownState.length
      : storeDropdownState
      ? 1
      : 0;
    return `${selectedCode}|${dropdownCount}`;
  }, [
    selectedStores?.storeDetails?.storeCode,
    selectedStores?.storeCode,
    selectedStores?.value,
    Array.isArray(storeDropdownState) ? storeDropdownState.length : 0,
  ]);

  useEffect(() => {
    voucherApiCall(startDate, endDate);
  }, [storeFetchDependencyKey, accessToken]);

  useEffect(() => {
    if (data && !isStoreActive && subVoucherPath !== "sub-voucher") {
      setTableData(data?.storeGetResponse || data?.storeWiseRecordsResponses || []);
    }
  }, [data, isStoreActive, subVoucherPath]);

  const onChangeDate = (dates) => {
    if (Array.isArray(dates)) {
      const [start, end] = dates;
      if (!start) return;
      setStartDate(start);
      setEndDate(end || null);
      if (end) voucherApiCall(start, end);
      return;
    }

    if (!dates) return;
    setStartDate(dates);
    setEndDate(dates);
    voucherApiCall(dates, dates);
  };

  const onClickChangeData = (data) => {
    setTableData(data);
  };

  const nonStoreColumns = useMemo(
    () => {
      const columns = l1Columns({
        navigate,
        editApprovalHandler: () => {},
        userActiveLevel: userLabel,
        approveRequestHandler: () => {},
        rejectRequestHandler: () => {},
        reworkRequestHandler: () => {},
        extraColumns: (subVoucherPath == "sub-voucher")
          ? [
              {
                Header: "V.ID",
                accessor: "id",
                Cell: (props) => {
                  const id = resolveVoucherListRowId(props?.row?.original);
                  const isLink = id != null && id !== "";
                  if (!isLink) {
                    return <span>-</span>;
                  }
                  return (
                    <div
                      className="underline underline-offset-4 cursor-pointer"
                      style={{
                        color: "blue",
                        pointerEvents: "auto",
                        userSelect: "none",
                      }}
                      onClick={() => {
                        const row = props?.row?.original;
                        navigate(`/vouchers/${id}`, {
                          state: { ...row, type: "Rework" },
                        });
                      }}
                    >
                      {id}
                    </div>
                  );
                },
              },
            ]
          : [
              {
                Header: "Store Name",
                accessor: "storeName",
                Cell: (props) => {
                  const storeName = props?.row?.original?.storeName;
                  return (
                    <div
                      className="underline underline-offset-4 cursor-pointer"
                      style={{ color: "blue" }}
                      onClick={() => {
                        setCurrentStoreSelect(storeName);
                        navigate("/vouchers/sub-voucher", {
                          state: { type: "Rework" },
                        });
                      }}
                    >
                      {storeName}
                    </div>
                  );
                },
              },
            ],
        inBetweenColumns: [],
      });
      // Remove Actions column from Rework History
      return columns.filter(col => col.accessor !== "actions");
    },
    [userLabel, navigate, subVoucherPath, setCurrentStoreSelect]
  );

  const storeColumnsData = useMemo(
    () =>
      storeColumns({
        onClickChangeData,
        navigate,
        navigationStateExtras: { type: "Rework" },
        downloadVoucher: (rowData, status) => {
          const st = `${moment(startDate).format("YYYY-MM-DD")} 00:00:00`;
          const et = `${moment(endDate).format("YYYY-MM-DD")} 23:59:59`;
          fileDownloadAtStore({
            url: `${apiUrls.dmsService.downloadVoucherStoreLevel}`,
            dataToSend: {
              storeCodes: [rowData?.storeCode],
              status: status,
              metadataSchemaId: 4,
              endDate: et,
              startDate: st,
            },
            method: "post",
            responseType: "blob",
            responseData: (res) => {
              const { data = "", status = null } = res || {};
              const contentType = res?.headers?.["content-type"];
              const contentDisposition = res?.headers["content-disposition"];
              const fileNameMatch =
                contentDisposition &&
                contentDisposition.match(/filename="(.+)"/);
              const fileName = fileNameMatch
                ? fileNameMatch[1]
                : `voucher_${status}_${moment().format("YYYY-MM-DD")}.xlsx`;

              if (status === 200) {
                downloadReportsFun(data, fileName, contentType);
              }
            },
          });
        },
      }),
    [onClickChangeData, navigate, startDate, endDate, fileDownloadAtStore]
  );

  const downloadVoucher = (rowData, status) => {
    const st = `${moment(startDate).format("YYYY-MM-DD")} 00:00:00`;
    const et = `${moment(endDate).format("YYYY-MM-DD")} 23:59:59`;
    fileDownloadAtStore({
      url: `${apiUrls.dmsService.downloadVoucherStoreLevel}`,
      dataToSend: {
        storeCodes: getArrayOfSelectedStoresCode(selectedStores, storeDropdownState),
        status: status,
        metadataSchemaId: 4,
        endDate: et,
        startDate: st,
      },
      method: "post",
      responseType: "blob",
      responseData: (res) => {
        const { data = "", status = null } = res || {};
        const contentType = res?.headers?.["content-type"];
        const contentDisposition = res?.headers["content-disposition"];
        const fileNameMatch =
          contentDisposition &&
          contentDisposition.match(/filename="(.+)"/);
        const fileName = fileNameMatch
          ? fileNameMatch[1]
          : `voucher_${status}_${moment().format("YYYY-MM-DD")}.xlsx`;

        if (status === 200) {
          downloadReportsFun(data, fileName, contentType);
        }
      },
    });
  };

  return (
    <div className="flex flex-col py-2">
      <div className="flex justify-end mt-2 mb-8">
        <div className="flex items-center gap-x-3">
          <div className="font-size-medium">Date Range</div>
          <DatePicker
            startDate={startDate}
            endDate={endDate}
            onChange={onChangeDate}
            maxDate={new Date()}
            dateRange
          />
        </div>
      </div>
      <CustomTable
        columns={isStoreActive ? storeColumnsData : nonStoreColumns}
        data={reworkTableRows}
        onCheckboxSelect={(checkboxes) => {
          if (reworkTableRows?.length) {
            setSelectedCheckBoxes(checkboxes);
          }
        }}
        showCheckboxes={false}
      />
    </div>
  );
};

export default ReworkHistory;
